// One widget's frame: a drag-handle header (title + player search + remove), the
// widget body, and a resize grip. Layout-agnostic — Canvas positions it
// absolutely and this just fills its box. React.memo'd so a drag mutating one
// widget's position never re-renders its siblings (the bento lesson).

import { memo } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { WidgetInstance, PlayerRef } from "../types.js";
import type { WidgetDef } from "../registry.js";
import PlayerSearch from "./PlayerSearch.js";
import { theme } from "../theme.js";

interface Props {
  instance: WidgetInstance;
  def: WidgetDef;
  /** True for one animation cycle right after this card absorbs a merge. */
  flash: boolean;
  onMoveStart: (e: ReactPointerEvent, instanceId: string) => void;
  onResizeStart: (e: ReactPointerEvent, instanceId: string) => void;
  onRemove: (instanceId: string) => void;
  onPickPlayer: (instanceId: string, ref: PlayerRef) => void;
  onPlayerDragStart: (e: ReactPointerEvent, ref: PlayerRef) => void;
  /** Drop feedback while something is dragged over this widget: "merge" = will
   *  combine into a comparison (electric blue), "ok" = a valid place/replace
   *  (green), "bad" = an invalid drop e.g. a duplicate/full merge (red), null =
   *  nothing hovering. */
  dropState: "merge" | "ok" | "bad" | null;
}

function WidgetShell({
  instance,
  def,
  flash,
  onMoveStart,
  onResizeStart,
  onRemove,
  onPickPlayer,
  onPlayerDragStart,
  dropState,
}: Props) {
  const Body = def.component;
  const currentName = instance.players[0]?.displayName ?? null;
  // Drop feedback, split into a solid edge colour + a SOFT rgba fill/glow — the
  // prototype pairs a solid border with an rgba(.4) fill, not a hard ring. Merge
  // is electric blue (combine into a comparison); place/replace green; invalid red.
  const glowEdge =
    dropState === "merge" ? theme.merge : dropState === "ok" ? theme.accent : dropState === "bad" ? theme.danger : null;
  const glowSoft =
    dropState === "merge" ? theme.mergeSoft : dropState === "ok" ? theme.accentSoft : dropState === "bad" ? theme.dangerSoft : null;

  return (
    <div
      className={flash ? "v2-merge-pop" : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        // Match the prototype's .placed-module: a top-lit gradient panel, not a
        // flat fill; #353535 border; 0 4px 10px shadow. Resting look only — the
        // drop-target glow (green = will merge, red = can't) still overrides.
        background: theme.moduleBg,
        border: `1px solid ${glowEdge ?? theme.moduleBorder}`,
        borderRadius: theme.radius,
        overflow: "hidden",
        // Glow = a thin solid edge + a SOFT rgba fill (inset) and outer glow —
        // the prototype's soft valid-placement look, not a hard 2px green ring.
        boxShadow: glowEdge
          ? `0 0 0 1px ${glowEdge}, inset 0 0 22px ${glowSoft}, 0 0 16px ${glowSoft}`
          : `0 4px 10px ${theme.shadowMd}`,
        transition: "box-shadow 0.1s, border-color 0.1s",
      }}
    >
      {/* Header = the drag handle. Interactive controls inside stopPropagation so
          they don't start a drag. */}
      <div
        onPointerDown={(e) => onMoveStart(e, instance.instanceId)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          // Prototype .module-header: fixed 40px tall, 0 15px padding, a faint
          // translucent strip over the panel, a near-black divider below.
          height: 40,
          flexShrink: 0,
          padding: "0 15px",
          background: theme.headerBg,
          borderBottom: `1px solid ${theme.headerBorder}`,
          cursor: "grab",
          touchAction: "none",
        }}
      >
        <span
          style={{
            // Prototype .module-title: body font, 0.7rem, weight 500, #ccc — a
            // quiet label, not the display-font uppercase we had.
            color: theme.inkMuted,
            fontFamily: theme.fontBody,
            fontSize: 11,
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          {def.title}
        </span>
        {def.needsPlayer ? (
          <PlayerSearch
            currentName={currentName}
            onPick={(ref) => onPickPlayer(instance.instanceId, ref)}
          />
        ) : (
          <span style={{ flex: 1 }} />
        )}
        <button
          aria-label="Remove widget"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onRemove(instance.instanceId)}
          style={{
            background: "transparent",
            border: "none",
            color: theme.inkFaint,
            fontSize: 16,
            lineHeight: 1,
            cursor: "pointer",
            padding: "0 2px",
          }}
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 15 }}>
        <Body
          instanceId={instance.instanceId}
          players={instance.players}
          state={instance.state}
          onPlayerDragStart={onPlayerDragStart}
        />
      </div>

      {/* Resize grip (bottom-right) — prototype .resize-handle: a 36px hit area
          with a small #666 corner bracket (.resize-handle::after). */}
      <div
        onPointerDown={(e) => onResizeStart(e, instance.instanceId)}
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 36,
          height: 36,
          cursor: "nwse-resize",
          touchAction: "none",
        }}
      >
        <span
          style={{
            position: "absolute",
            right: 5,
            bottom: 5,
            width: 8,
            height: 8,
            borderRight: `2px solid ${theme.resizeBracket}`,
            borderBottom: `2px solid ${theme.resizeBracket}`,
          }}
        />
      </div>
    </div>
  );
}

export default memo(WidgetShell);
